import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { ArrowLeft, Award, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ApiError } from "@eduviaos/shared";
import { Button, cn } from "@eduviaos/ui-kit";
import { Reveal, SectionCard } from "../../components/Section";
import { useSubmitSkillAssessment } from "../../lib/learning/hooks";
import type { SkillAssessmentResult, SkillAssessmentSession } from "../../lib/learning/types";
import { usePortalLocale } from "../../lib/i18n/usePortalLocale";
import { certificateVerifyPath } from "../../lib/learning/certificateVerify";
import { CertificateAttestationSummary } from "./CertificateAttestation";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

export function SkillAssessmentRunner({
  session,
  onClose,
}: {
  session: SkillAssessmentSession;
  onClose: () => void;
}) {
  const reduce = useReducedMotion();
  const { strings: s } = usePortalLocale();
  const submitMut = useSubmitSkillAssessment();
  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, string | null>>({});
  const [result, setResult] = React.useState<SkillAssessmentResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const questions = session.questions;
  const total = questions.length;
  const question = questions[Math.min(index, total - 1)];
  const isLast = index >= total - 1;

  if (result) {
    return (
      <div className="space-y-4">
        <SectionCard className="space-y-1 text-center">
          <p className="font-display text-lg font-semibold">
            {result.passed ? s.skillMastered : s.skillKeepPractising}
          </p>
          <p className="text-muted-foreground text-xs">
            {result.passed
              ? s.skillPassedScore(Math.round(result.score * 100), result.topic)
              : s.skillFailedScore(Math.round(result.score * 100))}
          </p>
        </SectionCard>
        {result.certificate && (
          <Reveal>
            <SectionCard className="flex items-start gap-3">
              <Award className="text-primary mt-0.5 size-5 shrink-0" />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-semibold">{s.certificateIssued}</p>
                <CertificateAttestationSummary cert={result.certificate} />
                <p className="text-muted-foreground break-all text-xs">
                  {s.verifyCodeLabel} {result.certificate.verify_code}
                </p>
                <Link
                  to={certificateVerifyPath(result.certificate.verify_code)}
                  className="text-primary hover:text-primary/80 focus-visible:ring-ring inline-flex items-center rounded text-xs font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2"
                >
                  {s.viewCertificate}
                </Link>
              </div>
            </SectionCard>
          </Reveal>
        )}
        <Button className="w-full" onClick={onClose}>
          {s.backToLearn}
        </Button>
      </div>
    );
  }

  if (!question) {
    return (
      <SectionCard className="space-y-3 text-center">
        <p className="text-sm font-semibold">{s.runnerNoQuestionsAssessment}</p>
        <Button variant="outline" onClick={onClose}>
          {s.back}
        </Button>
      </SectionCard>
    );
  }

  const selected = answers[question.id] ?? null;
  const progress = Math.round(((index + 1) / total) * 100);

  async function submit() {
    setError(null);
    try {
      const payload = questions.map((q) => ({
        question_id: q.id,
        selected_option_id: answers[q.id] ?? null,
      }));
      const res = await submitMut.mutateAsync({ sessionId: session.id, answers: payload });
      setResult(res);
    } catch (err) {
      // Raw backend messages are English-only, so they would break a Hindi
      // session on the one quiz that mints a certificate.
      setError(err instanceof ApiError && err.status === 0 ? s.errorOffline : s.runnerSubmitFailed);
    }
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors duration-150 ease-out"
      >
        <ArrowLeft className="size-3.5" />
        {s.back}
      </button>

      <SectionCard className="space-y-4">
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs">
            {session.subject} · {session.topic}
          </p>
          <div
            className="bg-muted h-1.5 overflow-hidden rounded-full"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={index + 1}
            aria-label={s.quizProgressAria(index + 1, total)}
          >
            <motion.div
              className="bg-primary h-full rounded-full"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: reduce ? 0 : 0.22, ease: EASE_OUT }}
            />
          </div>
          <p className="text-muted-foreground text-xs tabular-nums">
            {s.runnerQuestionOf(index + 1, total)}
          </p>
        </div>

        <p id={`sa-${question.id}`} className="text-sm font-medium leading-relaxed">
          {question.prompt}
        </p>

        {/* Labelled by the visible prompt rather than an sr-only legend: a
            legend would read the question twice to a screen reader. */}
        <fieldset className="space-y-2" aria-labelledby={`sa-${question.id}`}>
          {question.options.map((opt) => {
            const active = selected === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                aria-pressed={active}
                onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: opt.id }))}
                className={cn(
                  "duration-120 w-full rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ease-out",
                  active
                    ? "border-primary bg-primary/5 font-semibold"
                    : "border-border hover:bg-muted/40",
                )}
              >
                <span className="text-muted-foreground mr-2 font-medium" aria-hidden>
                  {opt.id}.
                </span>
                {opt.label}
              </button>
            );
          })}
        </fieldset>

        {error && (
          <p role="alert" className="text-destructive text-center text-xs font-medium">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          {index > 0 && (
            <Button
              variant="outline"
              className="flex-1"
              disabled={submitMut.isPending}
              onClick={() => setIndex((i) => i - 1)}
            >
              {s.runnerPrevious}
            </Button>
          )}
          {/* Skipping has to be reachable, or a learner who blanks on one
              question can never submit — and this is the quiz that decides
              whether a certificate gets issued. The BE already accepts a
              null answer and keeps it distinct from a wrong guess. */}
          <Button
            variant="ghost"
            className="flex-1"
            disabled={submitMut.isPending}
            onClick={() => {
              setAnswers((prev) => ({ ...prev, [question.id]: null }));
              if (!isLast) setIndex((i) => i + 1);
            }}
          >
            {s.skip}
          </Button>
          {!isLast ? (
            <Button
              className="flex-[2]"
              disabled={submitMut.isPending || !selected}
              onClick={() => setIndex((i) => i + 1)}
            >
              {s.runnerNext}
            </Button>
          ) : (
            <Button
              className="flex-[2]"
              disabled={submitMut.isPending}
              onClick={() => void submit()}
            >
              {submitMut.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {s.runnerSubmitting}
                </>
              ) : (
                s.runnerSubmitAssessment
              )}
            </Button>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
